import { cn } from '@/utils'
import wordmarkLight from '@/assets/brand/wordmark-light.svg'
import wordmarkDark from '@/assets/brand/wordmark-dark.svg'

type WordmarkSize = 'sm' | 'md' | 'lg'

/**
 * Height-driven sizing: the asset is a 816x112 viewBox lockup (~7.3:1), so the
 * width follows from the height and must never be set independently or the
 * mark distorts. `w-auto` + a height class is the whole contract.
 *
 *   sm  24px tall -> ~175px wide (sidebar brand bar: 256px minus px-6 = 208px)
 *   md  32px tall -> ~233px wide
 *   lg  48px tall -> ~350px wide (auth pages, inside a 448px max-w-md column)
 */
const SIZES: Record<WordmarkSize, string> = {
  sm: 'h-6',
  md: 'h-8',
  lg: 'h-12',
}

/**
 * The full "Vharta.ai Platform" lockup (V mark + wordmark), as opposed to
 * `Logo`, which is just the square V badge.
 *
 * Two assets, swapped on the app theme exactly like `Logo` does: the light
 * file is dark-inked for light surfaces, the dark file is light-inked for
 * dark ones. Both are transparent SVGs, so neither carries a background tile
 * that could seam against the surface behind it.
 *
 * Because the lockup already contains the wordmark, callers should NOT also
 * render a "Vharta.ai Platform" heading beside it — that duplicates both the
 * V mark and the words, visually and for screen readers.
 */
export function Wordmark({
  size = 'md',
  className,
}: {
  size?: WordmarkSize
  className?: string
}) {
  return (
    <span className={cn('inline-flex shrink-0 items-center', className)}>
      {/* Light mode: dark ink */}
      <img
        src={wordmarkLight}
        alt="Vharta.ai Platform"
        className={cn('block w-auto object-contain dark:hidden', SIZES[size])}
        draggable={false}
      />
      {/* Dark mode: light ink. aria-hidden so the name isn't announced twice —
          only one of the pair is ever displayed, but both are in the DOM. */}
      <img
        src={wordmarkDark}
        alt=""
        aria-hidden="true"
        className={cn('hidden w-auto object-contain dark:block', SIZES[size])}
        draggable={false}
      />
    </span>
  )
}
